import React, { type FC, useMemo } from 'react'
import { Popper } from '@mui/material'
import { ScatterPlot } from '@mui/icons-material'

import { copyObjectWithoutKeys, isArray, type ReadonlyRecord } from '../../../../utils'

import { type ConnectwareError, type RuleEngineEndpoint, Translation } from '../../../../domain'

import { Autocomplete, type WithIcon, type WithMeta } from '../../common'
import { useAppUsecase } from '../../State'
import { useEndpoints, useSelectedEndpoints } from './Hooks'

const noSelection = [] as const

const withOptionContent = (endpoint: RuleEngineEndpoint): WithMeta<WithIcon<RuleEngineEndpoint>> => ({
    ...endpoint,
    icon: <ScatterPlot />,
    meta: [endpoint.service, endpoint.connection].filter(Boolean).join(' · ') || undefined,
})

/**
 * Keeps already-selected endpoints selectable/removable even if they're missing from the latest fetch
 * (eg. deleted after being referenced, or filtered out by the current search). Left untouched while
 * loading/erroring so `NoOptionsText` still shows its spinner/error for those states.
 */
const withSelected = (
    endpoints: readonly RuleEngineEndpoint[] | ConnectwareError | null,
    selected: readonly RuleEngineEndpoint[]
): readonly WithMeta<WithIcon<RuleEngineEndpoint>>[] | ConnectwareError | null =>
    isArray(endpoints)
        ? Object.values(
            [...endpoints, ...selected].reduce<ReadonlyRecord<RuleEngineEndpoint['id'], WithMeta<WithIcon<RuleEngineEndpoint>>>>(
                (r, option) => ({ ...r, [option.id]: withOptionContent(option) }),
                {}
            )
        )
        : endpoints

export const EndpointsDropdown: FC = () => {
    const filter = useAppUsecase('filterSandboxUsecase')

    const endpoints = useEndpoints()
    const selected = useSelectedEndpoints() ?? noSelection

    const options = useMemo(() => withSelected(endpoints, selected), [endpoints, selected])
    const value = useMemo(() => selected.map(withOptionContent), [selected])

    return (
        <Autocomplete
            data-testid="rule-engine-endpoints-dropdown"
            multiple
            options={options}
            noOptionsText={Translation.NO_ENDPOINTS_AVAILABLE}
            value={value}
            getOptionLabel={(option) => option.name}
            /** `withOptionContent` recreates its object on every call, so options/value can't be matched by reference */
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onSearch={(term) => filter.setEndpointsSearch(term)}
            /** Strips the render-only `icon`/`meta` back off before it reaches app state, which has to stay framework-free/serializable */
            onSelect={(endpoints) => filter.selectEndpoints(endpoints.map((endpoint) => copyObjectWithoutKeys(endpoint, 'icon', 'meta')))}
            slots={{
                popper: (props) => <Popper data-testid="rule-engine-endpoints-dropdown-popper" {...props} />,
            }}
        />
    )
}
